import React from 'react'

import { Box, Stack, Typography, useTheme } from '@mui/material'
import { Switch } from '@repo/ui'

import { TooltipComponent } from './Tooltip'

/**
 * Width of one channel column. The header captions and every row's switch use
 * this same value, which is what keeps them in a straight vertical line — a
 * switch is 42px wide, so the column is sized to the control.
 */
export const CHANNEL_COLUMN_WIDTH = 42

/** Gap between channel columns. Shared so header and rows can't drift apart. */
export const CHANNEL_COLUMN_GAP = 4

export type PreferenceSwitch = {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  /**
   * Shown as a tap-to-open tooltip on a disabled switch — working-conventions
   * §27 (in-context actions are disabled + explained, never hidden).
   */
  disabledReason?: string
}

export type PreferenceRowProps = {
  title: string
  description: string
  /**
   * One entry per channel column, in the same order as the header's `labels`.
   * `null` renders an empty placeholder in that column (same width, no
   * control) — VRG-490: a row with fewer real switches than there are
   * channel columns (e.g. an SMS-only row) still needs every column
   * present so it stays aligned with the rows above and below it. Do not
   * hack this at the call site by shortening the array; a shortened array
   * maps left-to-right into the wrong columns.
   */
  switches: (PreferenceSwitch | null)[]
}

/**
 * Column captions ("Email", "SMS") for a preference grid.
 *
 * Rendered ONCE above the rows rather than repeated per row. Two reasons:
 * repeating them said the same thing on every line, and stacking a caption
 * directly above each switch left only 8px between them, which read as the
 * switch belonging to the caption rather than to its row.
 */
export const PreferenceChannelHeader = ({
  labels,
  sectionLabel,
}: {
  labels: string[]
  /**
   * Optional group caption rendered on the LEFT of this same row. Passed in
   * rather than stacked above by the caller so the caption and the column
   * captions share one line: they are both headers for the grid below, and on
   * separate lines they cost a whole row of vertical space while reading as two
   * unrelated fragments. Alignment stays correct because the columns keep their
   * own fixed widths on the right, independent of the caption's length.
   */
  sectionLabel?: React.ReactNode
}) => {
  const theme = useTheme()

  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent={sectionLabel ? 'space-between' : 'flex-end'}
      sx={{ pb: 1 }}
    >
      {sectionLabel}
      <Stack direction='row' spacing={CHANNEL_COLUMN_GAP}>
        {labels.map(label => (
          <Box key={label} sx={{ width: CHANNEL_COLUMN_WIDTH, textAlign: 'center' }}>
            <Typography variant='body3' sx={{ color: theme.palette.text.secondary }}>
              {label}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Stack>
  )
}

/**
 * A single "title + description ↔ switch(es)" preference row.
 *
 * Extracted on its second hand-roll (working-conventions §36) — first rolled
 * inline in CommunicationPreferences (VRG-139), then needed for the Scans /
 * Transfers toggles on the Notifications page (VERA-291).
 *
 * Switches are vertically centred against the row's text block, and each sits
 * in a fixed-width column so every row lines up with the header above it.
 */
export const PreferenceRow = ({ title, description, switches }: PreferenceRowProps) => {
  const theme = useTheme()

  return (
    <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ py: 2 }}>
      <Stack sx={{ pr: 3 }}>
        <Typography sx={{ color: theme.palette.text.primary, fontSize: '16px' }}>
          {title}
        </Typography>
        <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
          {description}
        </Typography>
      </Stack>

      <Stack direction='row' spacing={CHANNEL_COLUMN_GAP} alignItems='center'>
        {switches.map((entry, i) => (
          <Box
            key={i}
            sx={{ width: CHANNEL_COLUMN_WIDTH, display: 'flex', justifyContent: 'center' }}
          >
            {entry === null ? null : entry.disabled && entry.disabledReason ? (
              <TooltipComponent title={entry.disabledReason} placement='top'>
                {/* span wrapper: a disabled switch doesn't emit the tap events TooltipComponent needs */}
                <span style={{ display: 'inline-flex' }}>
                  <Switch checked={entry.checked} disabled />
                </span>
              </TooltipComponent>
            ) : (
              <Switch
                checked={entry.checked}
                disabled={entry.disabled}
                onChange={(_, next) => entry.onChange(next)}
              />
            )}
          </Box>
        ))}
      </Stack>
    </Stack>
  )
}
